import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../store/store";
import { getPaginatedAdsApi, getBookingsByAdApi, getBookingsByOwnerAdsApi } from "../../api/adsApi";
import { getLocalizedText } from "../../features/ads/i18nFields";
import type { Ad } from "../../features/ads/adsTypes";
import styles from "./MyAds.module.css";
import ShimmerImage from "../../components/ShimmerImage/ShimmerImage";
import { AD_IMAGE_PLACEHOLDER } from "../../utils/adImagePlaceholder";
import { getCountryValueDisplay, type CountryValue } from "../../features/country/countrySlice";

type ReservationUserItem = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  nbrChild: number | null;
  country: CountryValue;
  indicatif: string;
  codeCountry: string;
  dates: Array<{
    date: string;
    booked: boolean | null;
  }>;
};

type CalendarCell = {
  date: Date;
  inCurrentMonth: boolean;
};

const placeholder = AD_IMAGE_PLACEHOLDER;

const normalizeAd = (payload: any): Ad => {
  const raw = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const normalizedPhotos = Array.isArray(raw?.photos)
    ? raw.photos
        .map((photo: any) =>
          typeof photo === "string"
            ? photo
            : photo?.url || photo?.name || photo?.path || photo?.src
        )
        .filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  } as Ad;
};

const toDateKey = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const prefixedDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (prefixedDate) return prefixedDate[1];
    return null;
  }
  if (typeof value === "object") {
    const dateLike =
      (value as any)?.date ??
      (value as any)?.bookingDate ??
      (value as any)?.day ??
      null;
    if (!dateLike) return null;
    return toDateKey(dateLike);
  }
  return null;
};

const todayDateKey = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDate = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

const addMonths = (value: Date, months: number): Date =>
  new Date(value.getFullYear(), value.getMonth() + months, 1);

const formatDateKey = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateKey = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return normalizeDate(parsed);
};

const buildMonthCells = (monthDate: Date): CalendarCell[] => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPreviousMonth = new Date(year, month, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let i = firstDayIndex - 1; i >= 0; i -= 1) {
    cells.push({
      date: new Date(year, month - 1, daysInPreviousMonth - i),
      inCurrentMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(year, month, day),
      inCurrentMonth: true,
    });
  }

  let trailingDay = 1;
  while (cells.length < 42) {
    cells.push({
      date: new Date(year, month + 1, trailingDay),
      inCurrentMonth: false,
    });
    trailingDay += 1;
  }

  return cells;
};

const toBookedFlag = (value: unknown): boolean | null => {
  if (!value || typeof value !== "object") return null;
  if ("booked" in (value as Record<string, unknown>)) {
    const rawBooked = (value as { booked?: unknown }).booked;
    if (rawBooked === null) return null;
    if (typeof rawBooked === "boolean") return rawBooked;
  }
  return null;
};

const truncate = (text: string, max = 180): string => {
  if (!text) return "";
  const normalized = text.trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max).trimEnd()}...`;
};

const MyAdsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || "fr";
  const authUser = useSelector((state: RootState) => state.auth.user);
  const ownerId = authUser?.id || authUser?._id || "";

  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const PAGE_LIMIT = 12;

  const [popupAd, setPopupAd] = useState<Ad | null>(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState("");
  const [reservationUsers, setReservationUsers] = useState<ReservationUserItem[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [reservationsCountByAdId, setReservationsCountByAdId] = useState<Record<string, number>>({});
  const [reservationsCountLoadingByAdId, setReservationsCountLoadingByAdId] = useState<Record<string, boolean>>({});
  const [openDatesByUserId, setOpenDatesByUserId] = useState<Record<string, boolean>>({});
  const [visibleDatesByUserId, setVisibleDatesByUserId] = useState<Record<string, number>>({});
  const [dateFilterByUserId, setDateFilterByUserId] = useState<Record<string, string>>({});
  const [openDatePickerByUserId, setOpenDatePickerByUserId] = useState<Record<string, boolean>>({});
  const [datePickerMonthByUserId, setDatePickerMonthByUserId] = useState<Record<string, string>>({});
  const DATES_BATCH_SIZE = 20;
  const todayKey = useMemo(() => todayDateKey(), []);
  const weekDayLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    const monday = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }, (_, index) => {
      const current = new Date(monday);
      current.setDate(monday.getDate() + index);
      return formatter
        .format(current)
        .replace(".", "")
        .slice(0, 3);
    });
  }, [locale]);

  const loadOwnerAds = useCallback(
    async ({ cursor, append }: { cursor?: string | null; append: boolean }) => {
      if (!ownerId) return;

      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError("");
      }

      try {
        const response = await getPaginatedAdsApi({
          idOwner: String(ownerId),
          limit: PAGE_LIMIT,
          cursor: cursor || undefined,
        });
        const rawList = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextAds = rawList.map((entry: any) => normalizeAd(entry));
        const responseHasMore = Boolean(response?.data?.hasMore);
        const responseNextCursor = response?.data?.nextCursor ?? null;

        setAds((prev) => {
          if (!append) return nextAds;
          const byId = new Map<string, Ad>();
          prev.forEach((item) => {
            const id = item.id || (item as any)?._id;
            if (id) byId.set(String(id), item);
          });
          nextAds.forEach((item) => {
            const id = item.id || (item as any)?._id;
            if (id) byId.set(String(id), item);
          });
          return Array.from(byId.values());
        });
        setHasMore(responseHasMore);
        setNextCursor(responseNextCursor);
      } catch (err: any) {
        if (!append) setError(err?.response?.data?.message || t("myAds.loadError"));
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [ownerId, t]
  );

  useEffect(() => {
    if (!ownerId) {
      setAds([]);
      setHasMore(false);
      setNextCursor(null);
      setReservationsCountByAdId({});
      setReservationsCountLoadingByAdId({});
      return;
    }
    void loadOwnerAds({ append: false, cursor: null });
  }, [loadOwnerAds, ownerId]);

  useEffect(() => {
    if (!ownerId || ads.length === 0) return;

    const adIds = ads
      .map((ad) => String(ad.id || (ad as any)?._id || ""))
      .filter((adId) => adId);

    if (adIds.length === 0) return;

    setReservationsCountLoadingByAdId(() => {
      const next: Record<string, boolean> = {};
      adIds.forEach((adId) => {
        next[adId] = true;
      });
      return next;
    });

    let cancelled = false;
    const loadCounts = async () => {
      try {
        const response = await getBookingsByOwnerAdsApi(String(ownerId));
        const bookings = Array.isArray(response?.data?.data) ? response.data.data : [];
        const countByAd: Record<string, number> = {};
        adIds.forEach((adId) => {
          countByAd[adId] = 0;
        });

        bookings.forEach((booking: any) => {
          const adId = String(booking?.ads?.id || booking?.ads?._id || booking?.ads || "");
          const reservationUserId = String(booking?.user?.id || booking?.user?._id || "");
          if (!adId || !reservationUserId || reservationUserId === String(ownerId)) return;
          if (countByAd[adId] === undefined) return;
          countByAd[adId] += 1;
        });

        if (cancelled) return;
        setReservationsCountByAdId(countByAd);
      } catch {
        if (cancelled) return;
        setReservationsCountByAdId((prev) => {
          const next = { ...prev };
          adIds.forEach((adId) => {
            if (next[adId] === undefined) next[adId] = 0;
          });
          return next;
        });
      } finally {
        if (cancelled) return;
        setReservationsCountLoadingByAdId(() => {
          const next: Record<string, boolean> = {};
          adIds.forEach((adId) => {
            next[adId] = false;
          });
          return next;
        });
      }
    };

    void loadCounts();
    return () => {
      cancelled = true;
    };
  }, [ads, ownerId]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore || !nextCursor || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && nextCursor && !loadingMore) {
          void loadOwnerAds({ append: true, cursor: nextCursor });
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadOwnerAds, loading, loadingMore, nextCursor]);

  const mapBookingsToUsers = useCallback((bookings: any[]): ReservationUserItem[] => {
    const byUserId = new Map<string, ReservationUserItem>();

    (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
      const bookingUser = booking?.user;
      const userId = bookingUser?.id || bookingUser?._id;
      if (!userId) return;
      const key = String(userId);
      if (ownerId && key === String(ownerId)) return;

      if (!byUserId.has(key)) {
        byUserId.set(key, {
          id: key,
          firstName: bookingUser?.firstName || "",
          lastName: bookingUser?.lastName || "",
          email: bookingUser?.email || "",
          phone: bookingUser?.phone || "",
          status: bookingUser?.status || "",
          nbrChild: Number.isInteger(Number(bookingUser?.nbr_child)) ? Number(bookingUser?.nbr_child) : null,
          country: bookingUser?.country || "",
          indicatif: bookingUser?.indicatif || "",
          codeCountry: bookingUser?.codeCountry || "",
          dates: [],
        });
      }

      const targetUser = byUserId.get(key);
      if (!targetUser) return;

      const dates = Array.isArray(booking?.bookingDates) ? booking.bookingDates : [];
      dates.forEach((entry: any) => {
        const dateKey = toDateKey(entry?.date ?? entry);
        if (!dateKey) return;
        const booked = toBookedFlag(entry);
        const existingIndex = targetUser.dates.findIndex((dateItem) => dateItem.date === dateKey);
        if (existingIndex === -1) {
          targetUser.dates.push({ date: dateKey, booked });
          return;
        }

        // Prioritize confirmed when multiple rows exist for same date.
        const currentBooked = targetUser.dates[existingIndex].booked;
        const nextBooked =
          currentBooked === true || booked === true
            ? true
            : currentBooked === false || booked === false
              ? false
              : null;
        targetUser.dates[existingIndex] = { date: dateKey, booked: nextBooked };
      });
    });

    return Array.from(byUserId.values())
      .map((entry) => ({
        ...entry,
        dates: [...entry.dates].sort((a, b) => a.date.localeCompare(b.date)),
      }))
      .sort((a, b) => {
        const left = `${a.lastName} ${a.firstName}`.trim().toLowerCase();
        const right = `${b.lastName} ${b.firstName}`.trim().toLowerCase();
        return left.localeCompare(right);
      });
  }, [ownerId]);

  const openReservationsPopup = async (ad: Ad) => {
    if (!ad?.id) return;
    setPopupAd(ad);
    setPopupLoading(true);
    setPopupError("");
    setExpandedUserId(null);
    setOpenDatesByUserId({});
    setVisibleDatesByUserId({});
    setDateFilterByUserId({});
    setOpenDatePickerByUserId({});
    setDatePickerMonthByUserId({});

    try {
      const response = await getBookingsByAdApi(String(ad.id));
      const rawBookings = Array.isArray(response?.data?.data) ? response.data.data : [];
      setReservationUsers(mapBookingsToUsers(rawBookings));
    } catch (err: any) {
      setReservationUsers([]);
      setPopupError(err?.response?.data?.message || t("myAds.reservationsLoadError"));
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setPopupAd(null);
    setPopupLoading(false);
    setPopupError("");
    setReservationUsers([]);
    setExpandedUserId(null);
    setOpenDatesByUserId({});
    setVisibleDatesByUserId({});
    setDateFilterByUserId({});
    setOpenDatePickerByUserId({});
    setDatePickerMonthByUserId({});
  };

  const toggleUserDetails = (userId: string) => {
    setExpandedUserId((prev) => {
      const next = prev === userId ? null : userId;
      if (!next) {
        setOpenDatesByUserId((datesPrev) => ({ ...datesPrev, [userId]: false }));
      }
      return next;
    });
  };

  const toggleReservationDates = (userId: string, totalDates: number) => {
    setOpenDatesByUserId((prev) => {
      const nextOpen = !prev[userId];
      if (nextOpen) {
        setVisibleDatesByUserId((visiblePrev) => ({
          ...visiblePrev,
          [userId]: visiblePrev[userId] || Math.min(DATES_BATCH_SIZE, totalDates),
        }));
      } else {
        setOpenDatePickerByUserId((pickerPrev) => ({ ...pickerPrev, [userId]: false }));
      }
      return {
        ...prev,
        [userId]: nextOpen,
      };
    });
  };

  const handleDateMenuScroll = (
    userId: string,
    totalDates: number,
    event: React.UIEvent<HTMLDivElement>
  ) => {
    const target = event.currentTarget;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom > 24) return;

    setVisibleDatesByUserId((prev) => {
      const currentVisible = prev[userId] || Math.min(DATES_BATCH_SIZE, totalDates);
      if (currentVisible >= totalDates) return prev;
      return {
        ...prev,
        [userId]: Math.min(currentVisible + DATES_BATCH_SIZE, totalDates),
      };
    });
  };

  const toggleDatePicker = (userId: string) => {
    setOpenDatePickerByUserId((prev) => {
      const willOpen = !prev[userId];
      if (willOpen) {
        setDatePickerMonthByUserId((monthPrev) => {
          if (monthPrev[userId]) return monthPrev;
          const currentFilter = dateFilterByUserId[userId];
          const baseDate = currentFilter ? parseDateKey(currentFilter) : null;
          const monthStart = baseDate || new Date();
          return {
            ...monthPrev,
            [userId]: formatDateKey(new Date(monthStart.getFullYear(), monthStart.getMonth(), 1)),
          };
        });
      }
      return {
        ...prev,
        [userId]: willOpen,
      };
    });
  };

  const moveDatePickerMonth = (userId: string, delta: number) => {
    setDatePickerMonthByUserId((prev) => {
      const currentMonth = parseDateKey(prev[userId] || formatDateKey(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
      const nextMonth = addMonths(currentMonth || new Date(), delta);
      return {
        ...prev,
        [userId]: formatDateKey(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1)),
      };
    });
  };

  const applyDateFilter = (userId: string, dateKey: string, totalDates: number) => {
    const filteredCount = totalDates > 0 ? 1 : 0;
    setDateFilterByUserId((prev) => ({ ...prev, [userId]: dateKey }));
    setOpenDatePickerByUserId((prev) => ({ ...prev, [userId]: false }));
    setOpenDatesByUserId((prev) => ({ ...prev, [userId]: true }));
    setVisibleDatesByUserId((prev) => ({
      ...prev,
      [userId]: Math.min(DATES_BATCH_SIZE, filteredCount),
    }));
  };

  const clearDateFilter = (userId: string, totalDates: number) => {
    setDateFilterByUserId((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setVisibleDatesByUserId((prev) => ({
      ...prev,
      [userId]: Math.min(DATES_BATCH_SIZE, totalDates),
    }));
  };

  const popupTitle = useMemo(() => {
    if (!popupAd) return "";
    return getLocalizedText(popupAd.title_i18n, locale) || t("myAds.untitled");
  }, [locale, popupAd, t]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <h1>{t("myAds.title")}</h1>
          <p>{t("myAds.subtitle")}</p>
        </header>

        {loading && <p className={styles.state}>{t("myAds.loading")}</p>}
        {!loading && error && <p className={styles.state}>{t("myAds.error", { error })}</p>}
        {!loading && !error && ads.length === 0 && (
          <p className={styles.state}>{t("myAds.empty")}</p>
        )}

        {!loading && !error && ads.length > 0 && (
          <section className={styles.list}>
            {ads.map((ad) => {
              const adId = ad.id || (ad as any)?._id;
              if (!adId) return null;
              const adIdKey = String(adId);
              const reservationsCount = reservationsCountByAdId[adIdKey];
              const reservationsCountLoading = Boolean(reservationsCountLoadingByAdId[adIdKey]);
              const hasReservations = typeof reservationsCount === "number" ? reservationsCount > 0 : false;

              const title = getLocalizedText(ad.title_i18n, locale) || t("myAds.untitled");
              const description = truncate(getLocalizedText(ad.content_i18n, locale));
              const image = Array.isArray(ad.photos) && ad.photos[0] ? ad.photos[0] : placeholder;

              return (
                <Link key={String(adId)} className={styles.item} to={`/ads/${adId}`}>
                  <ShimmerImage className={styles.photo} src={image} alt={title} />
                  <div className={styles.content}>
                    <h3>{title}</h3>
                    <p>{description || t("myAds.noDescription")}</p>
                  </div>
                  <div className={styles.actionWrap}>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${hasReservations ? styles.actionBtnHasReservations : styles.actionBtnNoReservations}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!hasReservations) return;
                        void openReservationsPopup(ad);
                      }}
                    >
                      {reservationsCountLoading
                        ? t("myAds.loadingReservations")
                        : hasReservations
                          ? t("myAds.reservationsButton")
                          : t("myAds.noReservationsButton")}
                    </button>
                  </div>
                </Link>
              );
            })}
            {hasMore && (
              <div ref={sentinelRef} className={styles.moreState}>
                {loadingMore ? t("myAds.loadingMore") : t("myAds.scrollToLoad")}
              </div>
            )}
          </section>
        )}
      </div>

      {popupAd && (
        <div className={styles.popupOverlay} onClick={closePopup}>
          <div className={styles.popup} onClick={(event) => event.stopPropagation()}>
            <button type="button" className={styles.popupClose} onClick={closePopup} aria-label={t("myAds.closePopup")}>
              x
            </button>
            <h2>{t("myAds.reservationsFor", { title: popupTitle })}</h2>

            {popupLoading && <p className={styles.popupState}>{t("myAds.loadingReservations")}</p>}
            {!popupLoading && popupError && <p className={styles.popupState}>{t("myAds.error", { error: popupError })}</p>}
            {!popupLoading && !popupError && reservationUsers.length === 0 && (
              <p className={styles.popupState}>{t("myAds.noReservations")}</p>
            )}

            {!popupLoading && !popupError && reservationUsers.length > 0 && (
              <ul className={styles.userList}>
                {reservationUsers.map((reservationUser) => {
                  const fullName = `${reservationUser.lastName} ${reservationUser.firstName}`.trim() || t("myAds.unknownUser");
                  const isExpanded = expandedUserId === reservationUser.id;
                  const isDatesOpen = Boolean(openDatesByUserId[reservationUser.id]);
                  const dateFilter = dateFilterByUserId[reservationUser.id] || "";
                  const isDatePickerOpen = Boolean(openDatePickerByUserId[reservationUser.id]);
                  const filteredDates = dateFilter
                    ? reservationUser.dates.filter((dateItem) => dateItem.date === dateFilter)
                    : reservationUser.dates;
                  const visibleDatesCount =
                    visibleDatesByUserId[reservationUser.id] || Math.min(DATES_BATCH_SIZE, filteredDates.length);
                  const visibleDates = filteredDates.slice(0, visibleDatesCount);
                  const hasMoreDates = visibleDatesCount < filteredDates.length;
                  const monthBaseDate =
                    parseDateKey(datePickerMonthByUserId[reservationUser.id] || "") ||
                    parseDateKey(dateFilter) ||
                    new Date();
                  const monthDate = new Date(monthBaseDate.getFullYear(), monthBaseDate.getMonth(), 1);
                  const monthCells = buildMonthCells(monthDate);
                  const monthLabel = new Intl.DateTimeFormat(locale, {
                    month: "long",
                    year: "numeric",
                  }).format(monthDate);

                  return (
                    <li key={reservationUser.id} className={styles.userItem}>
                      <div className={styles.userTop}>
                        <button
                          type="button"
                          className={styles.userNameBtn}
                          onClick={() => toggleUserDetails(reservationUser.id)}
                        >
                          {fullName}
                        </button>
                      </div>

                      <div
                        className={`${styles.userDetails} ${isExpanded ? styles.userDetailsOpen : styles.userDetailsClosed}`}
                        aria-hidden={!isExpanded}
                      >
                        <div className={styles.userDetailsInner}>
                          <p><strong>{t("myAds.userEmail")}:</strong> {reservationUser.email || t("myAds.unknownField")}</p>
                          <p><strong>{t("myAds.userPhone")}:</strong> {`${reservationUser.indicatif || ""} ${reservationUser.phone || ""}`.trim() || t("myAds.unknownField")}</p>
                          <p><strong>{t("myAds.userStatus")}:</strong> {reservationUser.status || t("myAds.unknownField")}</p>
                          <p><strong>{t("myAds.userChildren")}:</strong> {reservationUser.nbrChild ?? t("myAds.unknownField")}</p>
                          <p><strong>{t("myAds.userCountry")}:</strong> {getCountryValueDisplay(reservationUser.country, reservationUser.codeCountry || t("myAds.unknownField"), locale)}</p>
                          <div className={styles.userDatesSection}>
                            <div className={styles.userDatesFilterRow}>
                              <button
                                type="button"
                                className={styles.userDatesToggleBtn}
                                onClick={() => toggleReservationDates(reservationUser.id, filteredDates.length)}
                              >
                                {isDatesOpen ? t("myAds.hideReservationDates") : t("myAds.showReservationDates")}
                              </button>
                              {isDatesOpen && (
                                <button
                                  type="button"
                                  className={styles.userDateFilterBtn}
                                  onClick={() => toggleDatePicker(reservationUser.id)}
                                >
                                  {dateFilter || t("myAds.filterByDate")}
                                </button>
                              )}
                              {isDatesOpen && dateFilter && (
                                <button
                                  type="button"
                                  className={styles.userDateFilterClearBtn}
                                  onClick={() => clearDateFilter(reservationUser.id, reservationUser.dates.length)}
                                >
                                  {t("myAds.clearDateFilter")}
                                </button>
                              )}
                            </div>
                            {isDatesOpen && isDatePickerOpen && (
                              <div className={styles.userDatePicker}>
                                <div className={styles.userDatePickerHeader}>
                                  <button
                                    type="button"
                                    className={styles.userDatePickerNav}
                                    onClick={() => moveDatePickerMonth(reservationUser.id, -1)}
                                    aria-label={t("myAds.previousMonth")}
                                  >
                                    ‹
                                  </button>
                                  <strong>{monthLabel}</strong>
                                  <button
                                    type="button"
                                    className={styles.userDatePickerNav}
                                    onClick={() => moveDatePickerMonth(reservationUser.id, 1)}
                                    aria-label={t("myAds.nextMonth")}
                                  >
                                    ›
                                  </button>
                                </div>
                                <div className={styles.userDatePickerWeekRow}>
                                  {weekDayLabels.map((dayLabel) => (
                                    <span key={`${reservationUser.id}-${dayLabel}`}>{dayLabel}</span>
                                  ))}
                                </div>
                                <div className={styles.userDatePickerGrid}>
                                  {monthCells.map((cell) => {
                                    const dateKey = formatDateKey(cell.date);
                                    const isSelected = dateFilter === dateKey;
                                    return (
                                      <button
                                        key={`${reservationUser.id}-${dateKey}`}
                                        type="button"
                                        className={`${styles.userDatePickerDay} ${
                                          !cell.inCurrentMonth ? styles.userDatePickerDayMuted : ""
                                        } ${isSelected ? styles.userDatePickerDaySelected : ""}`}
                                        onClick={() => applyDateFilter(reservationUser.id, dateKey, filteredDates.length)}
                                      >
                                        {cell.date.getDate()}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <div
                              className={`${styles.userDatesMenu} ${isDatesOpen ? styles.userDatesMenuOpen : styles.userDatesMenuClosed}`}
                              aria-hidden={!isDatesOpen}
                            >
                              {filteredDates.length === 0 ? (
                                <p className={styles.userDatesEmpty}>
                                  {dateFilter ? t("myAds.noDatesForFilter") : t("myAds.unknownDate")}
                                </p>
                              ) : (
                                <div
                                  className={styles.userDatesScrollArea}
                                  onScroll={(event) =>
                                    handleDateMenuScroll(reservationUser.id, filteredDates.length, event)
                                  }
                                >
                                  <ul className={styles.userDatesList}>
                                    {visibleDates.map((dateItem) => (
                                      <li key={`${reservationUser.id}-${dateItem.date}`} className={styles.userDateItem}>
                                        <span>{dateItem.date}</span>
                                        <span
                                          className={`${styles.userDateStatus} ${
                                            dateItem.date < todayKey
                                              ? styles.userDateStatusPast
                                              : dateItem.booked === true
                                              ? styles.userDateStatusConfirmed
                                              : dateItem.booked === false
                                                ? styles.userDateStatusPending
                                                : styles.userDateStatusUnknown
                                          }`}
                                        >
                                          {dateItem.booked === true
                                            ? t("myAds.statusConfirmed")
                                            : dateItem.booked === false
                                              ? t("myAds.statusPending")
                                              : t("myAds.statusUnknown")}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                  {hasMoreDates && (
                                    <p className={styles.userDatesLoadingMore}>{t("myAds.loadingMoreDates")}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAdsPage;
